import React, { FC, useCallback, useMemo } from 'react';
import { useTranslate } from '@/translations';
import { faDownload, Icon, CustomColumns, CustomTable } from '@/ui/components';

import { Inspection, InspectionStatusMap, TabDocument } from '@/types';

const link = 'https://bmwreports.gbatechnologies.net';

const getInitialData = (): TabDocument[] => [
  {
    id: 0,
    description: 'Mechanical Report initial',
    downloadLink: {
      getDownlaodLink: ({ vin }) =>
        `${link}/reports-initial/mechanical-report-initial-PDF/${vin}`,
    },
    fileName: 'Mechanical Report initial',
    link: {
      getHref: ({ vin }) =>
        `${link}/reports-initial/mechanical-report-initial/${vin}`,
      options: {
        target: '_blank',
        rel: 'noreferrer',
      },
    },
    show: true,
  },
  {
    id: 1,
    description: 'Vehicle Appraisal Report initial',

    downloadLink: {
      getDownlaodLink: ({ vin }) =>
        `${link}/reports-initial/appraisal-report-initial-PDF/${vin}`,
    },
    fileName: 'Vehicle Appraisal Report initial',
    link: {
      getHref: ({ vin }) =>
        `${link}/reports-initial/appraisal-report-initial/${vin}`,
      options: {
        target: '_blank',
        rel: 'noreferrer',
      },
    },
    show: true,
  },
];

const getApprovedData = ({ show }: { show: boolean }): TabDocument[] => [
  {
    id: 2,
    description: 'Mechanical Report approved',

    downloadLink: {
      getDownlaodLink: ({ vin }) =>
        `${link}/reports-final/mechanical-report-approved-PDF/${vin}`,
    },
    fileName: 'Mechanical Report approved',
    link: {
      getHref: ({ vin }) =>
        `${link}/reports-final/mechanical-report-approved/${vin}`,
      options: {
        /**
         * for external links only,
         * inner links opened in a new tab will not be shown
         * due to sessionStorage authentication policy,
         * user will be logged out
         */
        target: '_blank',
        rel: 'noreferrer',
      },
    },
    show,
  },
  {
    id: 3,
    description: 'Vehicle Appraisal Report approved',

    downloadLink: {
      getDownlaodLink: ({ vin }) =>
        `${link}/reports-final/appraisal-report-approved-PDF/${vin}`,
    },

    fileName: 'Vehicle Appraisal Report approved',
    link: {
      getHref: ({ vin }) =>
        `${link}/reports-final/appraisal-report-approved/${vin}`,
      options: {
        /**
         * for external links only,
         * inner links opened in a new tab will not be shown
         * due to sessionStorage authentication policy,
         * user will be logged out
         */
        target: '_blank',
        rel: 'noreferrer',
      },
    },
    show,
  },
];

export const DocumentsDataGrid: FC<{ inspection: Inspection }> = ({
  inspection,
}) => {
  const { t } = useTranslate();
  const inspectionId = inspection.id;

  const dataSource = useMemo(
    () => [
      ...getInitialData(),
      ...getApprovedData({
        show: [
          InspectionStatusMap.Authorised,
          InspectionStatusMap.AuthorisedOrderParts,
          InspectionStatusMap.Completed,
        ].includes(inspection.inspectionStatusName),
      }),
    ],
    [inspection.inspectionStatusName],
  );

  const vin = inspection.vin;

  const onRenderActionsCell = useCallback(
    ({ show, downloadLink }: TabDocument) => {
      const value = <Icon icon={faDownload} />;

      if (!show) return value;

      return (
        <a
          href={downloadLink?.getDownlaodLink({
            id: String(inspectionId),
            vin: vin || '',
          })}
          rel="noreferrer"
          download
        >
          {value}
        </a>
      );
    },
    [inspectionId, vin],
  );

  const onRenderDescriptionLink = useCallback(
    ({ show, description, link }: TabDocument) => {
      if (!show) return description;

      return (
        <a
          href={link.getHref({ id: String(inspectionId), vin: vin || '' })}
          target={link.options.target}
          rel={link.options.rel}
        >
          {description}
        </a>
      );
    },
    [inspectionId, vin],
  );

  const columns: CustomColumns<TabDocument> = useMemo(
    () => [
      {
        dataIndex: 'id',
        title: '',
        width: 50,
        align: 'center',
        render: (_, _1, index) => `${index + 1}.`,
      },
      {
        dataIndex: 'description',
        title: t('description'),
        render: (_, record) => onRenderDescriptionLink(record),
        filter: true,
      },
      {
        dataIndex: 'id',
        title: '',
        width: 50,
        align: 'center',
        render: (_, record) => onRenderActionsCell(record),
      },
    ],
    [onRenderActionsCell, onRenderDescriptionLink, t],
  );

  return <CustomTable dataSource={dataSource} columns={columns} rowKey="id" />;
};
